import { useCallback, useEffect, useState } from 'react';
import { api } from '../api';
import {
  mockListTipos,
  mockGetTipo,
  mockCreateTipo,
  mockUpdateTipo,
  mockDeleteTipo,
  mockSuggestPrefix,
} from '../data/tiposMock';

/**
 * useTiposCrud(scope)
 *
 * Gestión completa del catálogo de tipos para la pantalla III.4:
 * listar, crear, editar, borrar, y sugerir prefijo de referencia.
 *
 * ───────────────────────────────────────────────────────────────────────
 * MODO MOCK
 * ───────────────────────────────────────────────────────────────────────
 * El form de tipos usa campos que solo existen tras el bloque backend
 * II.4.5 (scope, has_stock, ref_prefix, ref_padding, tipo `select`).
 * Mientras II.4.5 no esté aplicado en la BBDD, USE_MOCK = true y todo
 * el CRUD opera sobre data/tiposMock.js (memoria de la pestaña).
 *
 * EL LUNES, tras aplicar II.4.5 y reiniciar el backend:
 *
 *     const USE_MOCK = false;
 *
 * y el hook tira de los endpoints reales del módulo (ya implementados
 * en el backend II.4.5 entregado):
 *
 *   GET    /api/inventario/tipos?scope=interno|colab
 *   GET    /api/inventario/tipos/suggest-prefix?slug=...
 *   POST   /api/inventario/tipos
 *   PATCH  /api/inventario/tipos/:id
 *   DELETE /api/inventario/tipos/:id?confirmDestructive=true
 *
 * El backend devuelve { data } / { data: [...] }; aquí se normaliza.
 * ───────────────────────────────────────────────────────────────────────
 */

const USE_MOCK = false;
const MOCK_DELAY = 250;

const wait = (ms) => new Promise((r) => setTimeout(r, ms));

export function useTiposCrud(scope) {
  const [tipos, setTipos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      if (USE_MOCK) {
        await wait(MOCK_DELAY);
        setTipos(mockListTipos({ scope }));
      } else {
        const res = await api.get('/tipos', scope ? { scope } : undefined);
        setTipos(res?.data || []);
      }
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
    }
  }, [scope]);

  useEffect(() => {
    load();
  }, [load]);

  const suggestPrefix = useCallback(async (slug) => {
    if (USE_MOCK) {
      await wait(120);
      return mockSuggestPrefix(slug);
    }
    const res = await api.get('/tipos/suggest-prefix', { slug, scope });
    return res?.prefix;
  }, [scope]);

  const getTipo = useCallback(async (id) => {
    if (USE_MOCK) {
      await wait(120);
      return mockGetTipo(id);
    }
    const res = await api.get(`/tipos/${id}`);
    return res?.data || res;
  }, []);

  const createTipo = useCallback(
    async (payload) => {
      let created;
      if (USE_MOCK) {
        await wait(MOCK_DELAY);
        created = mockCreateTipo(payload);
      } else {
        const res = await api.post('/tipos', payload);
        created = res?.data || res;
      }
      await load();
      // Avisar al sidebar global (useAssetTypes escucha este evento)
      // para que refresque su lista sin recargar la página.
      window.dispatchEvent(new Event('inventario:tipos-changed'));
      return created;
    },
    [load]
  );

  const updateTipo = useCallback(
    async (id, payload) => {
      let updated;
      if (USE_MOCK) {
        await wait(MOCK_DELAY);
        updated = mockUpdateTipo(id, payload);
      } else {
        // El FieldSchemaEditor ya pide confirmación EXPLÍCITA antes de
        // quitar un campo existente del esquema, así que cualquier DROP
        // COLUMN que llegue aquí ya fue confirmado por el usuario.
        // Pasamos el flag al backend para que aplique el diff destructivo
        // sin pedir un segundo round-trip de confirmación.
        const res = await api.put(`/tipos/${id}?confirmDestructive=true`, payload);
        updated = res?.data || res;
      }
      await load();
      window.dispatchEvent(new Event('inventario:tipos-changed'));
      return updated;
    },
    [load]
  );

  const deleteTipo = useCallback(
    async (id) => {
      if (USE_MOCK) {
        await wait(MOCK_DELAY);
        mockDeleteTipo(id, { confirmDestructive: true });
      } else {
        await api.del(`/tipos/${id}?confirmDestructive=true`);
      }
      await load();
      window.dispatchEvent(new Event('inventario:tipos-changed'));
      return true;
    },
    [load]
  );

  return {
    tipos,
    loading,
    error,
    refresh: load,
    suggestPrefix,
    getTipo,
    createTipo,
    updateTipo,
    deleteTipo,
    isMock: USE_MOCK,
  };
}
